import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { X, Calendar, Tag, Users, CheckSquare, MessageSquare, Paperclip, Clock, AlignLeft, Trash2, Archive, Plus, Edit2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Textarea } from '@/shared/components/ui/textarea';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { toast } from 'sonner';
import { cardApi, checklistApi, commentApi, attachmentApi } from '../services/eztaskApi';

const PRIORITY_OPTIONS = [
{ value: 'low', label: 'Low', color: 'bg-green-500' },
{ value: 'medium', label: 'Medium', color: 'bg-yellow-500' },
{ value: 'high', label: 'High', color: 'bg-orange-500' },
{ value: 'urgent', label: 'Urgent', color: 'bg-red-500' }];


export default function CardModal({ cardId, boardLabels, boardMembers, onClose, onUpdate }) {const { t } = useTranslation("eztask");
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [newComment, setNewComment] = useState('');
  const [newChecklistName, setNewChecklistName] = useState('');
  const [showAddChecklist, setShowAddChecklist] = useState(false);
  const [newItemName, setNewItemName] = useState({});

  useEffect(() => {
    const fetchCard = async () => {
      try {
        const data = await cardApi.get(cardId);
        setCard(data);
        setTitle(data.title);
        setDescription(data.description || '');
      } catch {
        toast.error('Failed to load card');
      } finally {
        setLoading(false);
      }
    };
    fetchCard();
  }, [cardId]);

  const handleUpdateTitle = async () => {
    if (!title.trim() || title === card.title) {
      setEditingTitle(false);
      return;
    }
    try {
      await cardApi.update(cardId, { title });
      setCard((prev) => ({ ...prev, title }));
      setEditingTitle(false);
      onUpdate();
    } catch {
      toast.error('Failed to update title');
    }
  };

  const handleUpdateDescription = async () => {
    if (description === card.description) {
      setEditingDesc(false);
      return;
    }
    try {
      await cardApi.update(cardId, { description });
      setCard((prev) => ({ ...prev, description }));
      setEditingDesc(false);
      onUpdate();
    } catch {
      toast.error('Failed to update description');
    }
  };

  const handleUpdatePriority = async (priority) => {
    try {
      await cardApi.update(cardId, { priority });
      setCard((prev) => ({ ...prev, priority }));
      onUpdate();
    } catch {
      toast.error('Failed to update priority');
    }
  };

  const handleUpdateDueDate = async (e) => {
    const dueDate = e.target.value ? new Date(e.target.value).toISOString() : null;
    try {
      await cardApi.update(cardId, { due_date: dueDate });
      setCard((prev) => ({ ...prev, due_date: dueDate }));
      onUpdate();
    } catch {
      toast.error('Failed to update due date');
    }
  };

  const handleToggleLabel = async (labelId) => {
    const hasLabel = card.labels?.some((l) => l.id === labelId);
    try {
      if (hasLabel) {
        await cardApi.removeLabel(cardId, labelId);
        setCard((prev) => ({ ...prev, labels: prev.labels.filter((l) => l.id !== labelId) }));
      } else {
        await cardApi.addLabel(cardId, labelId);
        const label = boardLabels.find((l) => l.id === labelId);
        setCard((prev) => ({ ...prev, labels: [...(prev.labels || []), label] }));
      }
      onUpdate();
    } catch {
      toast.error('Failed to update labels');
    }
  };

  const handleToggleMember = async (userId) => {
    const hasMember = card.members?.some((m) => m.user_id === userId);
    try {
      if (hasMember) {
        await cardApi.removeMember(cardId, userId);
        setCard((prev) => ({ ...prev, members: prev.members.filter((m) => m.user_id !== userId) }));
      } else {
        await cardApi.addMember(cardId, userId);
        const member = boardMembers.find((m) => m.user_id === userId);
        setCard((prev) => ({ ...prev, members: [...(prev.members || []), member] }));
      }
      onUpdate();
    } catch {
      toast.error('Failed to update members');
    }
  };

  const handleAddChecklist = async () => {
    if (!newChecklistName.trim()) return;
    try {
      const result = await checklistApi.create(cardId, { name: newChecklistName });
      setCard((prev) => ({
        ...prev,
        checklists: [...(prev.checklists || []), { ...result, items: [] }]
      }));
      setNewChecklistName('');
      setShowAddChecklist(false);
    } catch {
      toast.error('Failed to create checklist');
    }
  };

  const handleAddChecklistItem = async (checklistId) => {
    const name = newItemName[checklistId];
    if (!name?.trim()) return;
    try {
      const result = await checklistApi.addItem(checklistId, { name });
      setCard((prev) => ({
        ...prev,
        checklists: prev.checklists.map((cl) =>
        cl.checklist_id === checklistId ?
        { ...cl, items: [...cl.items, result] } :
        cl
        )
      }));
      setNewItemName((prev) => ({ ...prev, [checklistId]: '' }));
    } catch {
      toast.error('Failed to add item');
    }
  };

  const handleToggleChecklistItem = async (itemId, isComplete) => {
    try {
      await checklistApi.toggleItem(itemId, !isComplete);
      setCard((prev) => ({
        ...prev,
        checklists: prev.checklists.map((cl) => ({
          ...cl,
          items: cl.items.map((item) =>
          item.item_id === itemId ? { ...item, is_complete: !isComplete } : item
          )
        }))
      }));
      onUpdate();
    } catch {
      toast.error('Failed to update item');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    try {
      const result = await commentApi.create(cardId, newComment);
      setCard((prev) => ({
        ...prev,
        comments: [{ ...result, content: newComment, created_at: new Date().toISOString() }, ...(prev.comments || [])]
      }));
      setNewComment('');
    } catch {
      toast.error('Failed to add comment');
    }
  };

  const handleArchive = async () => {
    try {
      await cardApi.archive(cardId);
      onUpdate();
      onClose();
      toast.success('Card archived');
    } catch {
      toast.error('Failed to archive card');
    }
  };

  if (loading) {
    return (
      <Dialog open onOpenChange={onClose}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
          </div>
        </DialogContent>
      </Dialog>);

  }

  if (!card) return null;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="card-modal">
        <DialogHeader>
          <DialogTitle className="flex items-start gap-2">
            {editingTitle ?
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={handleUpdateTitle}
              onKeyDown={(e) => e.key === 'Enter' && handleUpdateTitle()}
              className="bg-slate-900 border-slate-600"
              autoFocus /> :


            <span
              className="text-xl cursor-pointer hover:text-blue-400 flex-1"
              onClick={() => setEditingTitle(true)}>
              
                {card.title}
              </span>
            }
          </DialogTitle>
          <p className="text-sm text-slate-400">{t("inList", "in list")}{card.list_name}</p>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-6 mt-4">
          {/* Main Content */}
          <div className="col-span-2 space-y-6">
            {/* Labels */}
            {card.labels?.length > 0 &&
            <div className="flex flex-wrap gap-2">
                {card.labels.map((label) =>
              <span
                key={label.id}
                className="px-3 py-1 rounded text-sm text-white"
                style={{ backgroundColor: label.color }}>
                
                    {label.name || 'Label'}
                  </span>
              )}
              </div>
            }

            {/* Description */}
            <div>
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <AlignLeft className="w-4 h-4" />{t("description", "Description")}

              </h4>
              {editingDesc ?
              <div>
                  <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="bg-slate-900 border-slate-600 mb-2"
                  rows={4}
                  autoFocus />
                
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleUpdateDescription}>{t("save", "Save")}</Button>
                    <Button variant="ghost" size="sm" onClick={() => {setEditingDesc(false);setDescription(card.description || '');}}>{t("cancel", "Cancel")}

                  </Button>
                  </div>
                </div> :

              <div
                className="bg-slate-900 rounded-lg p-3 text-sm text-slate-300 cursor-pointer hover:bg-slate-700 min-h-[60px]"
                onClick={() => setEditingDesc(true)}>
                
                  {card.description || 'Add a more detailed description...'}
                </div>
              }
            </div>

            {/* Checklists */}
            {card.checklists?.map((checklist) => {
              const total = checklist.items?.length || 0;
              const complete = checklist.items?.filter((i) => i.is_complete).length || 0;
              const progress = total > 0 ? complete / total * 100 : 0;

              return (
                <div key={checklist.checklist_id}>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <CheckSquare className="w-4 h-4" />
                    {checklist.name}
                    <span className="text-xs text-slate-400 ml-auto">{complete}/{total}</span>
                  </h4>
                  <div className="h-2 bg-slate-700 rounded-full mb-3 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${progress === 100 ? 'bg-green-500' : 'bg-blue-500'}`}
                      style={{ width: `${progress}%` }} />
                    
                  </div>
                  <div className="space-y-2">
                    {checklist.items?.map((item) =>
                    <div key={item.item_id} className="flex items-center gap-2">
                        <Checkbox
                        checked={item.is_complete}
                        onCheckedChange={() => handleToggleChecklistItem(item.item_id, item.is_complete)} />
                      
                        <span className={`text-sm ${item.is_complete ? 'line-through text-slate-500' : 'text-slate-300'}`}>
                          {item.name}
                        </span>
                      </div>
                    )}
                    <div className="flex gap-2 mt-2">
                      <Input
                        value={newItemName[checklist.checklist_id] || ''}
                        onChange={(e) => setNewItemName((prev) => ({ ...prev, [checklist.checklist_id]: e.target.value }))}
                        placeholder="Add an item..."
                        className="bg-slate-900 border-slate-600 text-sm"
                        onKeyDown={(e) => e.key === 'Enter' && handleAddChecklistItem(checklist.checklist_id)} />
                      
                      <Button size="sm" onClick={() => handleAddChecklistItem(checklist.checklist_id)}>{t("add", "Add")}</Button>
                    </div>
                  </div>
                </div>);

            })}

            {/* Comments */}
            <div>
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />{t("comments", "Comments")}

              </h4>
              <div className="flex gap-2 mb-4">
                <Textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Write a comment..."
                  className="bg-slate-900 border-slate-600 text-sm"
                  rows={2} />
                
              </div>
              <Button size="sm" onClick={handleAddComment} disabled={!newComment.trim()}>{t("addComment", "Add Comment")}

              </Button>
              <div className="mt-4 space-y-3">
                {card.comments?.map((comment) =>
                <div key={comment.id} className="bg-slate-900 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm">{comment.author_name}</span>
                      <span className="text-xs text-slate-500">
                        {new Date(comment.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-slate-300">{comment.content}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Activity */}
            {card.activities?.length > 0 &&
            <div>
                <h4 className="font-semibold mb-2">{t("activity", "Activity")}</h4>
                <div className="space-y-2">
                  {card.activities.map((activity) =>
                <div key={activity.id} className="text-sm text-slate-400 flex items-start gap-2">
                      <span className="text-slate-300 font-medium">{activity.actor_name}</span>
                      <span>{activity.description}</span>
                      <span className="text-xs ml-auto">
                        {new Date(activity.created_at).toLocaleString()}
                      </span>
                    </div>
                )}
                </div>
              </div>
            }
          </div>

          {/* Sidebar Actions */}
          <div className="space-y-4">
            {/* Priority */}
            <div>
              <h5 className="text-xs text-slate-400 mb-2 uppercase">{t("priority", "Priority")}</h5>
              <div className="space-y-1">
                {PRIORITY_OPTIONS.map((opt) =>
                <button
                  key={opt.value}
                  onClick={() => handleUpdatePriority(opt.value)}
                  className={`w-full text-left px-3 py-1.5 rounded text-sm flex items-center gap-2 ${
                  card.priority === opt.value ? 'bg-slate-700' : 'hover:bg-slate-700'}`
                  }>
                  
                    <span className={`w-3 h-3 rounded ${opt.color}`} />
                    {opt.label}
                  </button>
                )}
              </div>
            </div>

            {/* Due Date */}
            <div>
              <h5 className="text-xs text-slate-400 mb-2 uppercase flex items-center gap-1">
                <Calendar className="w-3 h-3" />{t("dueDate", "Due Date")}

              </h5>
              <Input
                type="datetime-local"
                value={card.due_date ? new Date(card.due_date).toISOString().slice(0, 16) : ''}
                onChange={handleUpdateDueDate}
                className="bg-slate-900 border-slate-600 text-sm" />
              
            </div>

            {/* Labels */}
            <div>
              <h5 className="text-xs text-slate-400 mb-2 uppercase flex items-center gap-1">
                <Tag className="w-3 h-3" />{t("labels", "Labels")}

              </h5>
              <div className="space-y-1">
                {boardLabels?.map((label) =>
                <button
                  key={label.id}
                  onClick={() => handleToggleLabel(label.id)}
                  className={`w-full text-left px-3 py-1.5 rounded text-sm flex items-center gap-2 ${
                  card.labels?.some((l) => l.id === label.id) ? 'ring-2 ring-white' : ''}`
                  }
                  style={{ backgroundColor: label.color }}>
                  
                    {label.name || 'Label'}
                  </button>
                )}
              </div>
            </div>

            {/* Members */}
            <div>
              <h5 className="text-xs text-slate-400 mb-2 uppercase flex items-center gap-1">
                <Users className="w-3 h-3" />{t("members", "Members")}

              </h5>
              <div className="space-y-1">
                {boardMembers?.map((member) =>
                <button
                  key={member.user_id}
                  onClick={() => handleToggleMember(member.user_id)}
                  className={`w-full text-left px-3 py-1.5 rounded text-sm flex items-center gap-2 hover:bg-slate-700 ${
                  card.members?.some((m) => m.user_id === member.user_id) ? 'bg-slate-700' : ''}`
                  }>
                  
                    <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-xs">
                      {member.user_name?.charAt(0).toUpperCase()}
                    </div>
                    {member.user_name}
                  </button>
                )}
              </div>
            </div>

            {/* Add Checklist */}
            <div>
              <h5 className="text-xs text-slate-400 mb-2 uppercase flex items-center gap-1">
                <CheckSquare className="w-3 h-3" />{t("checklist", "Checklist")}

              </h5>
              {showAddChecklist ?
              <div>
                  <Input
                  value={newChecklistName}
                  onChange={(e) => setNewChecklistName(e.target.value)}
                  placeholder="Checklist name..."
                  className="bg-slate-900 border-slate-600 text-sm mb-2"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddChecklist()}
                  autoFocus />
                
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleAddChecklist}>{t("add", "Add")}</Button>
                    <Button variant="ghost" size="sm" onClick={() => setShowAddChecklist(false)}>{t("cancel", "Cancel")}</Button>
                  </div>
                </div> :

              <Button variant="outline" size="sm" className="w-full" onClick={() => setShowAddChecklist(true)}>
                  <Plus className="w-4 h-4 mr-2" />{t("addChecklist", "Add Checklist")}

              </Button>
              }
            </div>

            {/* Archive */}
            <Button
              variant="destructive"
              size="sm"
              className="w-full"
              onClick={handleArchive}>
              
              <Archive className="w-4 h-4 mr-2" />{t("archiveCard", "Archive Card")}

            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>);

}